import styles from "pages/replacements-page-styles.module.css";
import CloseIcon from "@mui/icons-material/Close";
import { GridLegacy as Grid } from "@mui/material";
import { KeyboardEvent, MouseEvent, useMemo, useState } from "react";
import ReplaceOriginalItemBox from "../ReplaceOriginalItemBox";
import {
	NoReplacementPartsText,
	PartsAvailableForReplacementText,
	ReplacementsFlyoutSelectPartText
} from "constants/text";
import { useDispatch, useSelector } from "react-redux";
import { cartActions, Part } from "features/reducers/replacementOrder/cart.ts";
import { RootState } from "stores/application.store";
import PartsSearch from "../PartsSearch";
import FlyoutLineItemDetails from "../FlyoutLineItemDetails";
import ReplacePartBox from "../ReplacePartBox";
import FlyoutFooter from "../FlyoutFooter/FlyoutFooter.tsx";
import { calculateMaxCabinetsToBeReplaced, createReplaceableParts, sortReplacementParts } from "../ReplacementsUtil";
import {
	ReplacementsFlyoutHeader,
	ReplacementsFlyoutScrollableWrapper,
	ReplacementsFlyoutWrapper
} from "./ReplacementsFlyout.styles";
import { models } from "types/api/viewModels.ts";

interface ReplacementsFlyoutProps {
	lineItemInformation: models["LineItemViewModel"] | null | undefined;
	globalInformation: models["GlobalsViewModel"] | null | undefined;
	addToOrder: () => void;
	closeDrawer: (event: KeyboardEvent | MouseEvent) => void;
}

const ReplacementsFlyout = ({
	lineItemInformation,
	globalInformation: lineItemGlobals,
	addToOrder,
	closeDrawer
}: ReplacementsFlyoutProps) => {
	const draft = useSelector((state: RootState) => state.cart.draft);

	const originalItem = draft ?? {
		id: lineItemInformation?.id ?? "",
		itemNumber: lineItemInformation?.name ?? "",
		lineItemNumber: lineItemInformation?.lineItemNumber ?? 0,
		measurements: lineItemInformation?.measurements,
		description: lineItemInformation?.description ?? "",
		doorStyle: lineItemGlobals?.style?.description ?? null,
		lineItemId: lineItemInformation?.lineItemId ?? null,
		finish: lineItemGlobals?.finish?.description ?? null,
		modifications: lineItemInformation?.modifications ?? null,
		sku: lineItemInformation?.sku ?? "",
		construction: lineItemGlobals?.construction?.description ?? null,
		hinge: lineItemGlobals?.hingeType?.description ?? null,
		quantityOrdered: lineItemInformation?.quantityOrdered ?? 0,
		wholeCabinetQuantity: 0,
		cabinetFileUploads: [],
		replaceableParts: lineItemInformation?.replaceableParts
			? createReplaceableParts(lineItemInformation?.replaceableParts, lineItemInformation?.lineItemId!)
			: [],
		cabinetDefectCode: "",
		cabinetReplacementReason: null,
		cabinetReplacementProblem: null
	};

	const searchOptions = useMemo(() => {
		return lineItemInformation?.replaceableParts?.map(
			(replaceablePart) => replaceablePart.sku + " " + replaceablePart?.description
		);
	}, [lineItemInformation?.replaceableParts]);

	const [selectPartError, setSelectPartError] = useState(false);
	const [searchValue, setSearchValue] = useState<string>("");
	const dispatch = useDispatch();

	const maxCabinetsToBeReplaced = calculateMaxCabinetsToBeReplaced(originalItem);

	const totalParts = originalItem.replaceableParts.filter(
		(part: { partQuantity: number }) => part.partQuantity !== 0
	).length;

	const disableOriginalItemCheckbox = totalParts > 0 && maxCabinetsToBeReplaced === 0;

	const handleSearch = (_event: unknown, values: string) => {
		setSearchValue(values);
	};

	const handleSearchConditions = (parts: Part[]) => {
		const foundSearch = parts.find((part: Part) =>
			`${part.partSku.toUpperCase()} ${part.description.toUpperCase()}`.includes(searchValue.toUpperCase() ?? "")
		);
		return sortReplacementParts(
			parts.filter((replaceablePart) => {
				if (searchValue) {
					return (
						`${replaceablePart.partSku.toUpperCase()} ${replaceablePart.description.toUpperCase()}`.includes(
							searchValue.toUpperCase() ?? ""
						) || !foundSearch
					);
				} else {
					return true;
				}
			})
		);
	};

	const addToOrderHandler = () => {
		const totalParts = originalItem.replaceableParts.filter(
			(part: { partQuantity: number }) => part.partQuantity !== 0
		).length;

		if (totalParts === 0 && originalItem.wholeCabinetQuantity === 0) {
			setSelectPartError(true);
			return;
		}
		addToOrder();
		setSelectPartError(false);
		dispatch(cartActions.confirmEdit());
	};

	const handlePartError = (err: boolean) => {
		setSelectPartError(err);
	};

	return (
		<ReplacementsFlyoutWrapper data-testid="replacements-flyout-container">
			<ReplacementsFlyoutHeader data-testid="replacements-flyout-header">
				<h5 data-testid="replacements-flyout-header">{ReplacementsFlyoutSelectPartText}</h5>
				<CloseIcon
					onClick={closeDrawer}
					data-testid="replacements-flyout-close-button"
				/>
			</ReplacementsFlyoutHeader>

			<ReplacementsFlyoutScrollableWrapper>
				<div
					data-testid="replacements-flyout-body"
					className={styles.replacementsFlyoutBody}
				>
					<FlyoutLineItemDetails lineItem={originalItem} />
					<ReplaceOriginalItemBox
						maxCabinetsToBeReplaced={maxCabinetsToBeReplaced}
						lineItem={originalItem}
						disableOriginalItemCheckbox={disableOriginalItemCheckbox}
						handlePartError={handlePartError}
					/>
				</div>

				<div
					className={styles.partsAvailableForReplacementHeader}
					data-testid="replacements-flyout-parts-available-for-replacement-header"
				>
					<Grid
						container
						sx={{ width: "100%" }}
					>
						<Grid item>
							{originalItem.replaceableParts?.length >= 1 ? (
								<p className="body1">
									<span className={styles.partsAvailableForReplacementText}>
										{originalItem.replaceableParts?.length}
									</span>

									{PartsAvailableForReplacementText}
								</p>
							) : (
								<p className="body1">{NoReplacementPartsText}</p>
							)}
						</Grid>
					</Grid>
				</div>

				{originalItem.replaceableParts?.length > 0 && (
					<div className={styles.partsSearch}>
						<PartsSearch
							searchOptions={searchOptions}
							searchValue={searchValue}
							handleSearch={handleSearch}
						/>
					</div>
				)}
				<div data-testid="replacements-flyout-parts-available-for-replacement-map">
					{handleSearchConditions(originalItem.replaceableParts).map(
						(part) =>
							part && (
								<ReplacePartBox
									key={part.id}
									part={part}
									lineItemToEdit={originalItem}
									handlePartError={handlePartError}
								/>
							)
					)}
				</div>
			</ReplacementsFlyoutScrollableWrapper>
			<div
				className={styles.replacementPartsFlyoutButtonContainer}
				data-testid="replacements-flyout-button-container"
			>
				<FlyoutFooter
					addToOrderHandler={addToOrderHandler}
					closeDrawer={closeDrawer}
					selectPartError={selectPartError}
				/>
			</div>
		</ReplacementsFlyoutWrapper>
	);
};

export default ReplacementsFlyout;
